import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FloppyDiskIcon,
  XIcon,
  PackageIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { WorkbenchTransaction } from '../hooks/useWorkbench';
import { Transaction } from '@/api/rexel/ship-and-debit/useRexelAllTransactions';
import { useRexelShipAndDebitTransaction } from '@/api/rexel/ship-and-debit/useRexelShipAndDebitTransaction';
import { formatCurrency } from '@/utils/formatters';
import { format, isPast } from 'date-fns';

interface TransactionFormData {
  claimAmount: string;
  buyerCost: string;
  coreCostOverride: string;
  actualOverrideCost: string;
  listPrice: string;
  customerInvoiceAmount: string;
}

function formatTo4Decimals(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  // Round to 4 decimal places max, remove trailing zeros
  return parseFloat(num.toFixed(4)).toString();
}

function transactionToFormData(
  transaction: WorkbenchTransaction | null,
): TransactionFormData {
  if (!transaction) {
    return {
      claimAmount: '',
      buyerCost: '',
      coreCostOverride: '',
      actualOverrideCost: '',
      listPrice: '',
      customerInvoiceAmount: '',
    };
  }

  return {
    claimAmount: formatTo4Decimals(transaction.claimAmount),
    buyerCost: formatTo4Decimals(transaction.buyerCost),
    coreCostOverride: formatTo4Decimals(transaction.coreCostOverride),
    actualOverrideCost: formatTo4Decimals(transaction.actualOverrideCost),
    listPrice: formatTo4Decimals(transaction.listPrice),
    customerInvoiceAmount: formatTo4Decimals(transaction.customerInvoiceAmount),
  };
}

function MetadataItem({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

interface TransactionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: WorkbenchTransaction | null;
  onSave: (transactionId: string, updates: Partial<Transaction>) => void;
}

export function TransactionEditModal({
  isOpen,
  onClose,
  transaction,
  onSave,
}: TransactionEditModalProps) {
  const [formData, setFormData] = useState<TransactionFormData>(
    transactionToFormData(transaction),
  );
  const [isDirty, setIsDirty] = useState(false);
  const [inventoryPage, setInventoryPage] = useState(0);
  const INVENTORY_PAGE_SIZE = 10;

  // Fetch full transaction details for inventory and agreement info
  const { data: fullTransactionData, isLoading: isLoadingDetails } =
    useRexelShipAndDebitTransaction(isOpen ? transaction?.id : undefined);

  const fullTransaction = fullTransactionData?.transaction;

  useEffect(() => {
    setFormData(transactionToFormData(transaction));
    setIsDirty(false);
    setInventoryPage(0);
  }, [transaction]);

  const handleInputChange = (
    field: keyof TransactionFormData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSubmit = () => {
    if (!transaction) return;

    const updates: Partial<Transaction> = {};

    if (formData.claimAmount !== (transaction.claimAmount?.toString() ?? '')) {
      updates.claimAmount = formData.claimAmount
        ? parseFloat(formData.claimAmount)
        : null;
    }
    if (formData.buyerCost !== (transaction.buyerCost?.toString() ?? '')) {
      updates.buyerCost = formData.buyerCost
        ? parseFloat(formData.buyerCost)
        : null;
    }
    if (
      formData.coreCostOverride !==
      (transaction.coreCostOverride?.toString() ?? '')
    ) {
      updates.coreCostOverride = formData.coreCostOverride
        ? parseFloat(formData.coreCostOverride)
        : null;
    }
    if (
      formData.actualOverrideCost !==
      (transaction.actualOverrideCost?.toString() ?? '')
    ) {
      updates.actualOverrideCost = formData.actualOverrideCost
        ? parseFloat(formData.actualOverrideCost)
        : null;
    }
    if (formData.listPrice !== (transaction.listPrice?.toString() ?? '')) {
      updates.listPrice = formData.listPrice
        ? parseFloat(formData.listPrice)
        : null;
    }
    if (
      formData.customerInvoiceAmount !==
      (transaction.customerInvoiceAmount?.toString() ?? '')
    ) {
      updates.customerInvoiceAmount = formData.customerInvoiceAmount
        ? parseFloat(formData.customerInvoiceAmount)
        : null;
    }

    if (Object.keys(updates).length > 0) {
      onSave(transaction.id, updates);
    }
    onClose();
  };

  const handleClose = () => {
    setFormData(transactionToFormData(transaction));
    setIsDirty(false);
    onClose();
  };

  if (!transaction) return null;

  const isAgreementExpired =
    fullTransaction?.agreementEndDate &&
    isPast(new Date(fullTransaction.agreementEndDate));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="!max-w-[90vw] sm:!max-w-[1400px] max-h-[90vh]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Transaction - SO #{transaction.rexelSalesOrderNumber}
            {transaction.isModified && (
              <Badge variant="secondary" className="text-xs">
                Modified
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Review transaction details and modify pricing before sending.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4">
            {/* Overview Section */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Overview</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-5 gap-4">
                  <MetadataItem
                    label="Trading Partner"
                    value={transaction.tradingPartner || 'N/A'}
                  />
                  <MetadataItem
                    label="Contract Number"
                    value={transaction.supplierContractNumber || 'N/A'}
                  />
                  <MetadataItem
                    label="Invoice Number"
                    value={transaction.invoiceNumber || 'N/A'}
                  />
                  <MetadataItem
                    label="Division"
                    value={transaction.fileThreeLetterDivision || 'N/A'}
                  />
                  <MetadataItem
                    label="Ship Type"
                    value={
                      transaction.shipType ? (
                        <Badge variant="outline">{transaction.shipType}</Badge>
                      ) : (
                        'N/A'
                      )
                    }
                  />
                  <MetadataItem
                    label="Order Date"
                    value={
                      transaction.orderDate
                        ? format(new Date(transaction.orderDate), 'MMM d, yyyy')
                        : 'N/A'
                    }
                  />
                  <MetadataItem
                    label="Invoice Date"
                    value={
                      transaction.invoiceDate
                        ? format(
                            new Date(transaction.invoiceDate),
                            'MMM d, yyyy',
                          )
                        : 'N/A'
                    }
                  />
                  <MetadataItem
                    label="Ship Date"
                    value={
                      transaction.shipmentDate
                        ? format(
                            new Date(transaction.shipmentDate),
                            'MMM d, yyyy',
                          )
                        : 'N/A'
                    }
                  />
                  <MetadataItem
                    label="EDI Supplier"
                    value={
                      transaction.ediSupplierFlag === 'Y' ? (
                        <Badge
                          variant="secondary"
                          className="bg-green-100 text-green-700"
                        >
                          Yes
                        </Badge>
                      ) : (
                        'No'
                      )
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Customer, Item & Agreement Details */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Customer Details</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <MetadataItem
                      label="Customer Number"
                      value={transaction.customerNo || 'N/A'}
                    />
                    <MetadataItem
                      label="Customer Name"
                      value={transaction.customerName || 'N/A'}
                    />
                    <MetadataItem
                      label="Location"
                      value={
                        [
                          transaction.customerShipToCity,
                          transaction.customerShipToStateProvince,
                          transaction.customerShipToPostalCode,
                        ]
                          .filter(Boolean)
                          .join(', ') || 'N/A'
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Item Details</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <MetadataItem
                      label="Item Code"
                      value={transaction.itemInternalCode || 'N/A'}
                    />
                    <MetadataItem
                      label="Description"
                      value={
                        <span
                          className="line-clamp-2"
                          title={transaction.description || ''}
                        >
                          {transaction.description || 'N/A'}
                        </span>
                      }
                    />
                    <MetadataItem
                      label="Shipped Quantity"
                      value={transaction.shippedQuantity ?? 'N/A'}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Agreement Details
                    {isAgreementExpired && (
                      <Badge className="text-xs border-red-400 bg-red-100 text-red-700">
                        Expired
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {isLoadingDetails ? (
                    <div className="space-y-3">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <MetadataItem
                        label="Agreement Number"
                        value={fullTransaction?.agreementNumber || 'N/A'}
                      />
                      <MetadataItem
                        label="Agreement Type"
                        value={fullTransaction?.agreementType || 'N/A'}
                      />
                      <MetadataItem
                        label="Valid Period"
                        value={
                          fullTransaction?.agreementStartDate ||
                          fullTransaction?.agreementEndDate
                            ? `${
                                fullTransaction?.agreementStartDate
                                  ? format(
                                      new Date(
                                        fullTransaction.agreementStartDate,
                                      ),
                                      'MMM d, yyyy',
                                    )
                                  : 'N/A'
                              } - ${
                                fullTransaction?.agreementEndDate
                                  ? format(
                                      new Date(
                                        fullTransaction.agreementEndDate,
                                      ),
                                      'MMM d, yyyy',
                                    )
                                  : 'N/A'
                              }`
                            : 'N/A'
                        }
                      />
                      <MetadataItem
                        label="Reference Number"
                        value={
                          fullTransaction?.agreementReferenceNumber || 'N/A'
                        }
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Separator />

            {/* Pricing & Inventory side by side */}
            <div className="flex gap-4">
              {/* Editable Pricing Fields */}
              <Card className="border-primary/50 w-1/3">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Pricing Information
                    <Badge variant="outline" className="text-xs font-normal">
                      Editable
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="claimAmount" className="text-xs">
                        Claim Amount
                      </Label>
                      <Input
                        id="claimAmount"
                        type="number"
                        step="0.0001"
                        value={formData.claimAmount}
                        onChange={(e) =>
                          handleInputChange('claimAmount', e.target.value)
                        }
                        placeholder="0.0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="buyerCost" className="text-xs">
                        Buyer Cost
                      </Label>
                      <Input
                        id="buyerCost"
                        type="number"
                        step="0.0001"
                        value={formData.buyerCost}
                        onChange={(e) =>
                          handleInputChange('buyerCost', e.target.value)
                        }
                        placeholder="0.0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="coreCostOverride" className="text-xs">
                        Core Cost Override
                      </Label>
                      <Input
                        id="coreCostOverride"
                        type="number"
                        step="0.0001"
                        value={formData.coreCostOverride}
                        onChange={(e) =>
                          handleInputChange('coreCostOverride', e.target.value)
                        }
                        placeholder="0.0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="actualOverrideCost" className="text-xs">
                        Actual Override Cost
                      </Label>
                      <Input
                        id="actualOverrideCost"
                        type="number"
                        step="0.0001"
                        value={formData.actualOverrideCost}
                        onChange={(e) =>
                          handleInputChange(
                            'actualOverrideCost',
                            e.target.value,
                          )
                        }
                        placeholder="0.0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="listPrice" className="text-xs">
                        List Price
                      </Label>
                      <Input
                        id="listPrice"
                        type="number"
                        step="0.0001"
                        value={formData.listPrice}
                        onChange={(e) =>
                          handleInputChange('listPrice', e.target.value)
                        }
                        placeholder="0.0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="customerInvoiceAmount"
                        className="text-xs"
                      >
                        Customer Invoice Amount
                      </Label>
                      <Input
                        id="customerInvoiceAmount"
                        type="number"
                        step="0.0001"
                        value={formData.customerInvoiceAmount}
                        onChange={(e) =>
                          handleInputChange(
                            'customerInvoiceAmount',
                            e.target.value,
                          )
                        }
                        placeholder="0.0000"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Inventory Data */}
              <Card className="w-2/3">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <PackageIcon className="h-4 w-4" />
                    Inventory Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-0">
                  {isLoadingDetails ? (
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ) : fullTransaction?.inventoryData &&
                    fullTransaction.inventoryData.length > 0 ? (
                    (() => {
                      const sortedData = [
                        ...fullTransaction.inventoryData,
                      ].sort((a, b) =>
                        (b.idate ?? '').localeCompare(a.idate ?? ''),
                      );
                      const totalPages = Math.ceil(
                        sortedData.length / INVENTORY_PAGE_SIZE,
                      );
                      const paginatedData = sortedData.slice(
                        inventoryPage * INVENTORY_PAGE_SIZE,
                        (inventoryPage + 1) * INVENTORY_PAGE_SIZE,
                      );
                      return (
                        <div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>Branch</TableHead>
                                <TableHead>Supplier #</TableHead>
                                <TableHead>Invoice #</TableHead>
                                <TableHead>Cost</TableHead>
                                <TableHead>PO Cost</TableHead>
                                <TableHead>Receipt Cost</TableHead>
                                <TableHead className="text-right">
                                  Date
                                </TableHead>
                                <TableHead className="text-right">
                                  Qty
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedData.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell className="text-xs">
                                    {item.iprod || 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {item.fileThreeLetterDivision || 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {item.isupp || 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {item.iinvno || 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {item.icost
                                      ? formatCurrency(item.icost, {
                                          locale: 'en-CA',
                                          currency: 'CAD',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {item.ipocst
                                      ? formatCurrency(item.ipocst, {
                                          locale: 'en-CA',
                                          currency: 'CAD',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {item.ircost
                                      ? formatCurrency(item.ircost, {
                                          locale: 'en-CA',
                                          currency: 'CAD',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {item.idate
                                      ? format(
                                          new Date(
                                            parseInt(
                                              `20${item.idate.substring(0, 2)}`,
                                            ),
                                            parseInt(
                                              item.idate.substring(2, 4),
                                            ) - 1,
                                            parseInt(
                                              item.idate.substring(4, 6),
                                            ),
                                          ),
                                          'MMM d, yyyy',
                                        )
                                      : 'N/A'}
                                  </TableCell>
                                  <TableCell className="text-right text-xs">
                                    {item.iqty || 'N/A'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-2 border-t">
                              <span className="text-xs text-muted-foreground">
                                Showing{' '}
                                {inventoryPage * INVENTORY_PAGE_SIZE + 1}-
                                {Math.min(
                                  (inventoryPage + 1) * INVENTORY_PAGE_SIZE,
                                  sortedData.length,
                                )}{' '}
                                of {sortedData.length}
                              </span>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setInventoryPage((p) => Math.max(0, p - 1))
                                  }
                                  disabled={inventoryPage === 0}
                                >
                                  <CaretLeftIcon className="h-4 w-4" />
                                </Button>
                                <span className="text-xs px-2">
                                  Page {inventoryPage + 1} of {totalPages}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setInventoryPage((p) =>
                                      Math.min(totalPages - 1, p + 1),
                                    )
                                  }
                                  disabled={inventoryPage === totalPages - 1}
                                >
                                  <CaretRightIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="text-sm text-muted-foreground p-4">
                      No inventory data available
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            <XIcon className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isDirty}>
            <FloppyDiskIcon className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
